import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'

// action
import { accessControlActions, configActions } from '@redux/store/index'

// api
import { accessControlApi, accessGroupApi, i18nApi, ResType } from '@api'

// lib
import { Logger, UserList } from '@utils'

// schema
import {
	AccessControl,
	AccessControlType,
	AccessGroup,
	AccessGroupType,
	User,
	I18nType,
	UserType,
} from '@schemas'

// type
import type { AccessControlStateType, ApiGetDataStatus } from '@redux/types'

const apiAddAccessControl = async (body: any): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessControlApi.addAccessControl({
		body: body,
	})
	return res
}

const apiGetAccessControl = async (query: {
	user_id?: string
	id?: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessControlApi.getAccessControl({
		query: query,
	})
	return res
}

const apiUpdateAccessControl = async (
	id: string,
	body: any,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessControlApi.updateAccessControl({
		params: { id: id },
		body: body,
	})
	return res
}

const apiDeleteAccessControl = async (params: any): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessControlApi.deleteAccessControl({
		params: params,
	})
	return res
}

const apiGetUserFromAccessControl = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessControlApi.getUserFromAccessControl()
	return res
}

// api group
const apiGetAccessGroup = async (query: any): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessGroupApi.getAccessGroup({ query: query })
	return res
}

const apiAddAccessGroup = async (body: any): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessGroupApi.addAccessGroup({
		body: body,
	})
	return res
}

const apiUpdateAccessGroup = async (
	id: string,
	body: any,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessGroupApi.updateAccessGroup({
		params: { id: id },
		body: body,
	})
	return res
}

const apiUpdateAccessGroupUsers = async (
	id: string,
	body: any,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessGroupApi.updateAccessGroupUsers({
		params: { id: id },
		body: body,
	})
	return res
}

const apiDeleteAccessGroup = async (id: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await accessGroupApi.deleteAccessGroup({
		params: { id: id },
	})
	return res
}

// api i18n
const apiUpdateI18n = async (paramsId: string, body: any) => {
	const res: ResType | undefined = await i18nApi.updateI18n({
		params: { id: paramsId },
		body: body,
	})
	return res
}

function* initPage() {
	try {
		yield put(accessControlActions.TRIGGER_GET_GROUP(true))
		yield put(accessControlActions.TRIGGER_GET_USERS(true))
		yield put(accessControlActions.LEFT_MODAL_TAB_INDEX(0))
		yield put(accessControlActions.SHOW_SIDE_MODAL(false))
	} catch (error) {
		Logger.warn(error)
	}
}

function* getUsers() {
	try {
		const userList: UserType[] = yield select(state => state.accessControl.userList)

		if (userList.length === 0) {
			yield put(accessControlActions.USER_LIST_STATUS('loading'))
		}

		const apiRes: ResType | undefined = yield call(apiGetUserFromAccessControl)

		if (apiRes !== undefined && apiRes.status === 200) {
			const newUserList: UserType[] = apiRes.data.map(
				aUser => new User(aUser as UserType),
			)

			UserList.listSort(newUserList)

			yield put(accessControlActions.USER_LIST(newUserList))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* getGroups() {
	try {
		const groupList: AccessGroupType[] = yield select(
			state => state.accessControl.groupList,
		)
		const userLanguage: string = yield select(state => state.user.language)

		if (groupList.length === 0) {
			yield put(accessControlActions.GROUP_LIST_STATUS('loading'))
		}

		const apiRes: ResType | undefined = yield call(apiGetAccessGroup, {})

		if (apiRes !== undefined && apiRes.status === 200) {
			const newGroupList: AccessGroupType[] = apiRes.data.map(
				aGroup =>
					new AccessGroup({
						...aGroup,
						i18n: aGroup.i18n ?? [],
						users: aGroup.users ?? [],
					}),
			)

			newGroupList.sort((a: AccessGroupType, b: AccessGroupType) => {
				const i18nATitleArr = a.i18n.filter(
					anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase(),
				)
				const i18nBTitleArr = b.i18n.filter(
					anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase(),
				)
				const aTitle = i18nATitleArr.length > 0 ? i18nATitleArr[0].title : a.name
				const bTitle = i18nBTitleArr.length > 0 ? i18nBTitleArr[0].title : b.name
				const order = aTitle < bTitle ? -1 : aTitle > bTitle ? 1 : 0

				return order
			})

			yield put(accessControlActions.GROUP_LIST(newGroupList))

			const selectedTab: string = yield select(state => state.accessControl.selectedTab)
			const selectedAccessGroup: AccessControlStateType['selectedAccessGroup'] =
				yield select(state => state.accessControl.selectedAccessGroup)
			const selectedAccessGroupByUserTab: AccessGroupType = yield select(
				state => state.accessControl.selectedAccessGroupByUserTab,
			)

			const thisGroup = newGroupList.filter(aGroup =>
				selectedTab === 'group'
					? aGroup.id === selectedAccessGroup.id
					: aGroup.id === selectedAccessGroupByUserTab.id,
			)

			if (thisGroup.length > 0) {
				const newGroup = thisGroup[0]

				yield put(accessControlActions.SELECTED_ACCESS_GROUP({ ...newGroup }))
			}

			if (selectedTab === 'user') {
				const selectedUser: UserType = yield select(
					state => state.accessControl.selectedAccessUser,
				)

				const newGroupListByUserTab = newGroupList.filter(aGroup => {
					const userIds: string[] = aGroup.users.map(aUser => aUser.id)
					const isIncluded: boolean = userIds.includes(selectedUser.id)
					return isIncluded
				})

				yield put(accessControlActions.GROUP_LIST_BY_USER_TAB(newGroupListByUserTab))
				yield put(accessControlActions.GROUP_LIST_STATUS('ok'))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* selectedTabSaga() {
	try {
		const selectedTab: string = yield select(state => state.accessControl.selectedTab)

		const newLeftModalInfo: AccessControlStateType['leftModalInfo'] =
			selectedTab === 'group' ? { target: 'groupEditor' } : { target: 'groupSelector' }

		yield put(accessControlActions.LEFT_MODAL_INFO(newLeftModalInfo))
		yield put(accessControlActions.SHOW_SIDE_MODAL(false))
	} catch (error) {
		Logger.warn(error)
	}
}

function* userListSaga() {
	try {
		const userList: UserType[] = yield select(state => state.accessControl.userList)

		if (userList.length === 0) {
			yield put(accessControlActions.USER_LIST_STATUS('empty'))
		} else {
			yield put(accessControlActions.USER_LIST_STATUS('ok'))
		}

		yield put(accessControlActions.USER_SECTION_LIST([{ users: userList }]))
	} catch (error) {
		Logger.warn(error)
	}
}

function* groupListSaga() {
	try {
		const groupList: AccessGroupType[] = yield select(
			state => state.accessControl.groupList,
		)

		if (groupList.length === 0) {
			yield put(accessControlActions.GROUP_LIST_STATUS('empty'))
		} else {
			yield put(accessControlActions.GROUP_LIST_STATUS('ok'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* addAccessGroup() {
	try {
		const newEditGroup: AccessGroupType = yield select(
			state => state.accessControl.updateGroup,
		)

		const body = {
			name: newEditGroup.name,
		}

		yield put(accessControlActions.USER_LIST_STATUS('loading'))

		const res: ResType | null = yield call(apiAddAccessGroup, body)
		if (res !== null && res.status === 201) {
			yield put(
				configActions.TOAST({ isShow: true, message: '권한 그룹이 생성되었습니다.' }),
			)
			yield put(accessControlActions.TRIGGER_GET_GROUP(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateAccessGroup() {
	try {
		const newEditGroup: AccessGroupType = yield select(
			state => state.accessControl.updateGroup,
		)

		const body = {
			name: newEditGroup.name,
		}

		yield put(accessControlActions.USER_LIST_STATUS('loading'))

		const res: ResType | null = yield call(apiUpdateAccessGroup, newEditGroup.id, body)
		if (res !== null && res.status === 201) {
			yield put(
				configActions.TOAST({ isShow: true, message: '권한 그룹이 수정되었습니다.' }),
			)
			yield put(accessControlActions.TRIGGER_GET_GROUP(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* deletetAccessGroup() {
	try {
		const deleteGroupId: string = yield select(state => state.accessControl.deleteGroup)

		const res: ResType | null = yield call(apiDeleteAccessGroup, deleteGroupId)

		yield put(accessControlActions.USER_LIST_STATUS('loading'))

		if (res !== null && res.status === 200) {
			yield put(
				configActions.TOAST({ isShow: true, message: '권한 그룹이 삭제되었습니다.' }),
			)
			yield put(accessControlActions.TRIGGER_GET_GROUP(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateAccessGroupI18n() {
	try {
		const i18n: I18nType = yield select(
			state => state.accessControl.updateAccessGroupI18n,
		)

		const body = {
			tableName: i18n.tableName,
			tableId: i18n.tableId,
			menuId: i18n.menuId,
			isoLanguageCode: i18n.isoLanguageCode,
			value: i18n.value,
			title: i18n.title,
		}

		const res: ResType | undefined = yield call(apiUpdateI18n, i18n.id, body)

		if (res !== undefined && res.status === 201) {
			yield put(accessControlActions.TRIGGER_GET_GROUP(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* selectedAccessUserSaga() {
	try {
		const selectedUser: UserType = yield select(
			state => state.accessControl.selectedAccessUser,
		)
		const userLanguage: string = yield select(state => state.user.language)

		if (selectedUser.id !== '') {
			const apiRes: ResType | undefined = yield call(apiGetAccessGroup, {
				user_id: selectedUser.id,
			})

			if (apiRes !== undefined && apiRes.status === 200) {
				const newGroupList: AccessGroupType[] = apiRes.data.map(
					aGroup =>
						new AccessGroup({
							...aGroup,
							i18n: aGroup.i18n ?? [],
							users: aGroup.users ?? [],
						}),
				)

				newGroupList.sort((a: AccessGroupType, b: AccessGroupType) => {
					const i18nATitleArr = a.i18n.filter(
						anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase(),
					)
					const i18nBTitleArr = b.i18n.filter(
						anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase(),
					)
					const aTitle = i18nATitleArr.length > 0 ? i18nATitleArr[0].title : a.name
					const bTitle = i18nBTitleArr.length > 0 ? i18nBTitleArr[0].title : b.name
					const order = aTitle < bTitle ? -1 : aTitle > bTitle ? 1 : 0

					return order
				})

				yield put(accessControlActions.GROUP_LIST_BY_USER_TAB(newGroupList))
				yield put(
					accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB(new AccessGroup()),
				)
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* selectedAccessGroupSaga() {
	try {
		const selectedGroup: AccessControlStateType['selectedAccessGroup'] = yield select(
			state => state.accessControl.selectedAccessGroup,
		)

		if (selectedGroup.id !== '') {
			const query = { id: selectedGroup.id }

			const apiRes: ResType | undefined = yield call(apiGetAccessControl, query)

			if (apiRes !== undefined && apiRes.status === 200) {
				const result: AccessControlType[] = apiRes.data.map(
					aData => new AccessControl({ ...aData, menuI18n: aData.menuI18n ?? [] }),
				)

				yield put(accessControlActions.SELECTED_GROUP_ACCESS_CONTROL(result))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* selectedAccessGroupByUserTabSaga() {
	try {
		const selectedGroup: AccessGroupType = yield select(
			state => state.accessControl.selectedAccessGroupByUserTab,
		)

		if (selectedGroup.id !== '') {
			const query = { id: selectedGroup.id }

			const apiRes: ResType | undefined = yield call(apiGetAccessControl, query)

			if (apiRes !== undefined && apiRes.status === 200) {
				const result: AccessControlType[] = apiRes.data.map(
					aData => new AccessControl({ ...aData, menuI18n: aData.menuI18n ?? [] }),
				)

				yield put(accessControlActions.SELECTED_GROUP_ACCESS_CONTROL_BY_USER_TAB(result))
			}
		} else {
			yield put(accessControlActions.SELECTED_GROUP_ACCESS_CONTROL_BY_USER_TAB([]))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* groupAccessControlSaga() {
	try {
		const status: ApiGetDataStatus = yield select(
			state => state.accessControl.groupAccessControlStatus,
		)
		const selectedGroupAccessControl: AccessControlType[] = yield select(
			state => state.accessControl.selectedGroupAccessControl,
		)

		if (status === 'loading' && selectedGroupAccessControl.length === 0) {
			yield put(accessControlActions.GROUP_ACCESS_CONTROL_STATUS('empty'))
		} else if (status === 'loading' && selectedGroupAccessControl.length > 0) {
			yield put(accessControlActions.GROUP_ACCESS_CONTROL_STATUS('ok'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* groupAccessControlByUserTabSaga() {
	try {
		const selectedGroupAccessControl: AccessControlType[] = yield select(
			state => state.accessControl.selectedGroupAccessControlByUserTab,
		)

		if (selectedGroupAccessControl.length === 0) {
			yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('empty'))
		} else if (selectedGroupAccessControl.length > 0) {
			yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('ok'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* addAccessControl() {
	try {
		const addInfo: AccessControlStateType['addAccessControl'] = yield select(
			state => state.accessControl.addAccessControl,
		)
		const selectedGroup: AccessGroupType = yield select(
			state => state.accessControl.selectedAccessGroup,
		)
		const selectedTab: string = yield select(state => state.accessControl.selectedTab)

		const body = {
			accessGroupId: selectedGroup.id,
			menuId: addInfo.menuId,
			crud: addInfo.crud,
		}

		const apiRes: ResType | undefined = yield call(apiAddAccessControl, body)

		if (apiRes !== undefined && apiRes.status === 201) {
			yield put(configActions.TOAST({ isShow: true, message: '권한이 생성되었습니다.' }))

			if (selectedTab === 'user') {
				yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('loading'))
			} else if (selectedTab === 'group') {
				yield put(accessControlActions.GROUP_ACCESS_CONTROL_STATUS('loading'))
			}
			yield put(accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB(selectedGroup))
			yield put(accessControlActions.SELECTED_ACCESS_GROUP(selectedGroup))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateAccessControl() {
	try {
		const updateInfo: AccessControlStateType['updateAccessControl'] = yield select(
			state => state.accessControl.updateAccessControl,
		)
		const selectedGroup: AccessGroupType = yield select(
			state => state.accessControl.selectedAccessGroup,
		)
		const selectedTab: string = yield select(state => state.accessControl.selectedTab)

		const id: string = updateInfo.accessControlId
		const body = {
			accessGroupId: selectedGroup.id,
			menuId: updateInfo.menuId,
			crud: updateInfo.crud,
		}

		const apiRes: ResType | undefined = yield call(apiUpdateAccessControl, id, body)

		if (apiRes !== undefined && apiRes.status === 201) {
			yield put(configActions.TOAST({ isShow: true, message: '권한이 수정되었습니다.' }))

			if (selectedTab === 'user') {
				yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('loading'))
			} else if (selectedTab === 'group') {
				yield put(accessControlActions.GROUP_ACCESS_CONTROL_STATUS('loading'))
			}
			yield put(accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB(selectedGroup))
			yield put(accessControlActions.SELECTED_ACCESS_GROUP(selectedGroup))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* deleteAccessControl() {
	try {
		const deleteAccessControl: string = yield select(
			state => state.accessControl.deleteAccessControl,
		)
		const selectedTab: string = yield select(state => state.accessControl.selectedTab)

		const selectedGroup: AccessControlStateType['selectedAccessGroup'] = yield select(
			state => state.accessControl.selectedAccessGroup,
		)

		const params = {
			id: deleteAccessControl,
		}

		const apiRes: ResType | undefined = yield call(apiDeleteAccessControl, params)

		if (apiRes !== undefined && apiRes.status === 200) {
			yield put(configActions.TOAST({ isShow: true, message: '권한이 삭제되었습니다.' }))

			if (selectedTab === 'user') {
				yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('loading'))
			} else if (selectedTab === 'group') {
				yield put(accessControlActions.GROUP_ACCESS_CONTROL_STATUS('loading'))
			}
			yield put(accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB(selectedGroup))
			yield put(accessControlActions.SELECTED_ACCESS_GROUP(selectedGroup))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateAccessGroupUsers() {
	try {
		const updateInfo: AccessControlStateType['updateAccessGroupUsers'] = yield select(
			state => state.accessControl.updateAccessGroupUsers,
		)
		const selectedTab: string = yield select(state => state.accessControl.selectedTab)

		const groupId: string = updateInfo.accessGroupId
		const body = {
			userIds: updateInfo.userIds,
		}

		const apiRes: ResType | undefined = yield call(
			apiUpdateAccessGroupUsers,
			groupId,
			body,
		)

		if (apiRes !== undefined && apiRes.status === 201) {
			if (selectedTab === 'group') {
				yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('loading'))
			} else {
				yield put(accessControlActions.USER_ACCESS_CONTROL_STATUS('loading'))
			}

			yield put(accessControlActions.TRIGGER_GET_GROUP(true))
			yield put(accessControlActions.SHOW_SIDE_MODAL(false))
			yield put(configActions.TOAST({ isShow: true, message: '권한이 수정되었습니다.' }))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	// trigger
	yield takeEvery(accessControlActions.TRIGGER_INIT_ACCESS_CONTROL, initPage)
	yield takeEvery(accessControlActions.TRIGGER_GET_USERS, getUsers)
	yield takeEvery(accessControlActions.TRIGGER_GET_GROUP, getGroups)

	// tab
	yield takeEvery(accessControlActions.SELECTED_TAB, selectedTabSaga)

	// user list
	yield takeEvery(accessControlActions.USER_LIST, userListSaga)

	// group list
	yield takeEvery(accessControlActions.GROUP_LIST, groupListSaga)
	yield takeEvery(accessControlActions.ADD_GROUP, addAccessGroup)
	yield takeEvery(accessControlActions.UPDATE_GROUP, updateAccessGroup)
	yield takeEvery(accessControlActions.DELETE_GROUP, deletetAccessGroup)
	yield takeEvery(accessControlActions.UPDATE_ACCESS_GROUP_I18N, updateAccessGroupI18n)

	// get access control
	yield takeEvery(accessControlActions.SELECTED_ACCESS_GROUP, selectedAccessGroupSaga)
	yield takeEvery(accessControlActions.SELECTED_ACCESS_USER, selectedAccessUserSaga)
	yield takeEvery(
		accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB,
		selectedAccessGroupByUserTabSaga,
	)

	// access control
	yield takeEvery(
		accessControlActions.SELECTED_GROUP_ACCESS_CONTROL,
		groupAccessControlSaga,
	)
	yield takeEvery(
		accessControlActions.SELECTED_GROUP_ACCESS_CONTROL_BY_USER_TAB,
		groupAccessControlByUserTabSaga,
	)

	// access control action
	yield takeEvery(accessControlActions.ADD_ACCESS_CONTROL, addAccessControl)
	yield takeEvery(accessControlActions.UPDATE_ACCESS_CONTROL, updateAccessControl)
	yield takeEvery(accessControlActions.DELETE_ACCESS_CONTROL, deleteAccessControl)
	yield takeEvery(accessControlActions.UPDATE_ACCESS_GROUP_USERS, updateAccessGroupUsers)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
